import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Search, Plus, ChevronRight, AlertTriangle,
  CheckCircle2, Sparkles, Tag, Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import {
  DataTable,
  EmptyState,
  IconButton,
  type ColumnDef,
  type Row,
} from '@/components/ui'
import type { Article, ArticleGroup, ArticleListResponse } from '../shared.ts'
import { formatCurrency } from '../shared.ts'
import { AiTextModal } from './AiTextsSection.tsx'

// ─── Comparison types ────────────────────────────────────────────────────────

interface TenantValue {
  value: string
  conflictId?: string
  hasConflict: boolean
}

interface ComparisonField {
  field: string
  label: string
  localValue: string
  tenantValues: Record<string, TenantValue>
}

interface ComparisonData {
  tenants: Array<{ id: string; code: string }>
  fields: ComparisonField[]
}

// ─── Expanded row component ──────────────────────────────────────────────────

function ArticleComparisonRow({ article }: { article: Article }) {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery<ComparisonData>({
    queryKey: ['article-comparison', article.id],
    queryFn: () => apiClient.get(`/articles/${article.id}/comparison`).then((r) => r.data),
    staleTime: 30_000,
  })

  const resolveMutation = useMutation({
    mutationFn: ({ conflictId, resolution }: { conflictId: string; resolution: 'local' | 'remote' }) =>
      apiClient.post(`/articles/conflicts/${conflictId}/resolve`, { resolution }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-comparison', article.id] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  if (isLoading) {
    return (
      <div className="px-6 py-3 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Vergleich…
      </div>
    )
  }
  if (error || !data) {
    return <div className="px-6 py-3 text-sm text-red-600">Fehler beim Laden der Vergleichsdaten.</div>
  }
  if (data.fields.length === 0) {
    return <div className="px-6 py-3 text-sm text-gray-500 italic">Keine offenen Konflikte.</div>
  }

  return (
    <div className="px-6 pb-4 pt-2">
      <div className="rounded-lg border border-amber-200 bg-amber-50 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-amber-200 bg-amber-100/60">
              <th className="text-left px-3 py-2 font-semibold text-gray-600 w-28">Feld</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600">Lokal (zentral)</th>
              {data.tenants.map((t) => (
                <th key={t.id} className="text-left px-3 py-2 font-semibold text-gray-600">{t.code}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.fields.map((f) => (
              <tr key={f.field} className="border-b border-amber-100 last:border-0">
                <td className="px-3 py-2 text-gray-500 font-medium">{f.label}</td>
                <td className="px-3 py-2 font-mono text-gray-800">{f.localValue || <span className="text-gray-400 italic">leer</span>}</td>
                {data.tenants.map((t) => {
                  const tv = f.tenantValues[t.id]
                  if (!tv) return <td key={t.id} className="px-3 py-2 text-gray-300 italic">—</td>
                  if (!tv.hasConflict) {
                    return (
                      <td key={t.id} className="px-3 py-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      </td>
                    )
                  }
                  return (
                    <td key={t.id} className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-amber-800 bg-amber-100 rounded px-1 py-0.5 block">
                          {tv.value || <span className="italic text-amber-400">leer</span>}
                        </span>
                        {tv.conflictId && (
                          <div className="flex gap-1">
                            <button
                              className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                              disabled={resolveMutation.isPending}
                              onClick={() => resolveMutation.mutate({ conflictId: tv.conflictId!, resolution: 'local' })}
                              title="Lokalen Wert behalten und an SAP schreiben"
                            >
                              Lokal
                            </button>
                            <button
                              className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                              disabled={resolveMutation.isPending}
                              onClick={() => resolveMutation.mutate({ conflictId: tv.conflictId!, resolution: 'remote' })}
                              title="SAP-Wert übernehmen"
                            >
                              SAP
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Article List ───────────────────────────────────────────────────────────

export function ArticleListSection() {
  const [search, setSearch] = useState('')
  const [groupFilter, setGroupFilter] = useState<string>('')
  const [conflictOnly, setConflictOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [aiArticle, setAiArticle] = useState<Article | null>(null)
  const pageSize = 50

  const { data: groups = [] } = useQuery<ArticleGroup[]>({
    queryKey: ['article-groups'],
    queryFn: () => apiClient.get('/articles/groups').then((r) => r.data),
    retry: false,
  })

  const { data: articleResponse, isLoading } = useQuery<ArticleListResponse>({
    queryKey: ['articles', search, groupFilter, conflictOnly, page],
    queryFn: () =>
      apiClient
        .get('/articles', {
          params: {
            q: search || undefined,
            groupId: groupFilter || undefined,
            syncStatus: conflictOnly ? 'conflict' : undefined,
            page,
            pageSize,
          },
        })
        .then((r) => r.data),
    retry: false,
  })

  const articles = articleResponse?.data ?? []
  const total = articleResponse?.total ?? 0
  const totalPages = Math.ceil(total / pageSize)

  // Preisgruppen-Zugehoerigkeit fuer aktuelle Seite
  const articleIds = articles.map((a) => a.id)
  const { data: priceGroupMemberships = {} } = useQuery<Record<string, { priceGroupId: string; priceGroupName: string }>>({
    queryKey: ['price-group-memberships', articleIds],
    queryFn: () =>
      articleIds.length > 0
        ? apiClient.post('/articles/price-groups/memberships-lookup', { articleIds }).then((r) => r.data)
        : Promise.resolve({}),
    enabled: articleIds.length > 0,
    staleTime: 30_000,
  })

  const columns = useMemo<ColumnDef<Article, unknown>[]>(
    () => [
      {
        accessorKey: 'itemCode',
        header: 'ItemCode',
        cell: ({ row }) => <span className="font-mono text-gray-700" title={row.original.itemCode}>{row.original.itemCode}</span>,
      },
      {
        accessorKey: 'itemName',
        header: 'Bezeichnung',
        cell: ({ row }) => (
          <p className="font-medium text-gray-900 truncate max-w-[300px]" title={row.original.itemName}>{row.original.itemName}</p>
        ),
      },
      {
        accessorKey: 'groupName',
        header: 'Gruppe',
        cell: ({ row }) => {
          const pg = priceGroupMemberships[row.original.id]
          return (
            <div className="flex flex-col gap-0.5">
              <span className="truncate max-w-[200px] block" title={row.original.groupName || ''}>{row.original.groupName || '—'}</span>
              {pg && (
                <span className="inline-flex items-center gap-1 text-xs text-brand-600 font-medium bg-brand-50 rounded px-1.5 py-0.5 w-fit">
                  <Tag className="w-3 h-3" />{pg.priceGroupName}
                </span>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'manufacturer',
        header: 'Hersteller',
        cell: ({ row }) => (
          <span className="truncate max-w-[150px] block" title={row.original.manufacturer || ''}>{row.original.manufacturer || '—'}</span>
        ),
      },
      {
        accessorKey: 'purchasePrice',
        header: 'EK',
        cell: ({ row }) => <span className="block text-right tabular-nums">{formatCurrency(row.original.purchasePrice)}</span>,
      },
      {
        accessorKey: 'salesPrice',
        header: 'VK',
        cell: ({ row }) => <span className="block text-right tabular-nums font-medium">{formatCurrency(row.original.salesPrice)}</span>,
      },
      {
        accessorKey: 'margin',
        header: 'Marge',
        cell: ({ row }) => (
          <span className={cn('block text-right tabular-nums font-medium', row.original.margin > 0 ? 'text-green-600' : 'text-red-600')}>
            {row.original.margin.toFixed(1)}%
          </span>
        ),
      },
      {
        accessorKey: 'unit',
        header: 'Einheit',
        cell: ({ row }) => <span className="block text-center">{row.original.unit}</span>,
      },
      {
        accessorKey: 'stockQuantity',
        header: 'Bestand',
        cell: ({ row }) => <span className="block text-right tabular-nums">{row.original.stockQuantity ?? '—'}</span>,
      },
      {
        accessorKey: 'syncStatus',
        header: 'Sync',
        enableSorting: false,
        cell: ({ row }) => {
          const art = row.original
          const localTs = art.locallyModifiedAt
            ? new Date(art.locallyModifiedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
            : null
          return (
            <span className="flex justify-center">
              {art.syncStatus === 'synced' && (
                <span title={localTs ? `Lokal geändert: ${localTs}` : undefined}>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </span>
              )}
              {art.syncStatus === 'conflict' && (
                <span title={localTs ? `Lokale Änderung ausstehend (${localTs}), SAP noch nicht aktualisiert` : 'Konflikt: SAP-Synchronisierung ausstehend'}>
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                </span>
              )}
              {art.syncStatus === 'local_only' && <span className="text-caption text-gray-400">Lokal</span>}
            </span>
          )
        },
      },
      {
        id: '__actions__',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              icon={<Sparkles className="w-4 h-4" />}
              aria-label="KI-Text generieren"
              variant="ghost"
              size="xs"
              className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
              onClick={() => setAiArticle(row.original)}
            />
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        ),
      },
    ],
    [priceGroupMemberships, setAiArticle],
  )

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Artikelname, Nummer oder Hersteller suchen..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <select
          value={groupFilter}
          onChange={(e) => { setGroupFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        >
          <option value="">Alle Gruppen</option>
          {(() => {
            const sections: { label: string; items: ArticleGroup[] }[] = []
            let current: { label: string; items: ArticleGroup[] } = { label: 'Ungekategorisiert', items: [] }
            for (const g of groups) {
              if (g.isTitle) {
                if (current.items.length > 0) sections.push(current)
                current = { label: g.name, items: [] }
              } else {
                current.items.push(g)
              }
            }
            if (current.items.length > 0) sections.push(current)
            return sections.map((s) => (
              <optgroup key={s.label} label={s.label}>
                {s.items
                  .slice()
                  .sort((a, b) => (a.groupCode ?? a.sapGroupCode ?? 0) - (b.groupCode ?? b.sapGroupCode ?? 0))
                  .map((g) => {
                    const code = g.groupCode ?? g.sapGroupCode
                    return (
                      <option key={g.id} value={g.id}>{code ? `${code} – ` : ''}{g.name}</option>
                    )
                  })}
              </optgroup>
            ))
          })()}
        </select>
        <button
          onClick={() => { setConflictOnly((v) => !v); setPage(1) }}
          className={cn(
            'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
            conflictOnly
              ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50',
          )}
          title="Nur Artikel mit offenen SAP-Konflikten anzeigen"
        >
          <AlertTriangle className="w-4 h-4" />
          Nur Konflikte
        </button>
        <button className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" />Neuer Artikel
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <DataTable<Article>
          data={articles}
          columns={columns}
          getRowId={(a) => String(a.id)}
          loading={isLoading}
          emptyState={
            <EmptyState
              title="Keine Artikel gefunden"
              description="Starte einen SAP Sync um Artikel zu laden."
            />
          }
          enableSorting={false}
          getRowCanExpand={(row: Row<Article>) => row.original.syncStatus === 'conflict'}
          renderExpandedRow={(row: Row<Article>) => <ArticleComparisonRow article={row.original} />}
          rowClassName={(row: Article) =>
            row.syncStatus === 'conflict' ? 'bg-amber-50/60 hover:bg-amber-50' : undefined
          }
        />

        {/* Server-side pagination (DataTable handles client-side only) */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">{total} Artikel gesamt</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Zurück
              </button>
              <span className="text-sm text-gray-600">Seite {page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>

      {aiArticle && (
        <AiTextModal article={aiArticle} onClose={() => setAiArticle(null)} />
      )}
    </div>
  )
}
